import React, { useMemo, useState } from "react";
import { Chart } from "primereact/chart";
import AiOperationalInsights from "../../components/AiOperationalInsights/AiOperationalInsights";

// ── helpers ──────────────────────────────────────────────────────────────────

function parseHours(str) {
    if (!str) return 0;
    const parts = String(str).split(":").map(Number);
    return (parts[0] || 0) + (parts[1] || 0) / 60;
}

function fmtHours(h) {
    const hrs = Math.floor(h);
    const mins = Math.round((h - hrs) * 60);
    return `${hrs}:${String(mins).padStart(2, "0")}`;
}

// ── stat card ─────────────────────────────────────────────────────────────────

function StatCard({ icon, badge, badgeColor, value, label, sub }) {
    return (
        <div
            style={{
                flex: 1,
                background: "#fff",
                border: "1px solid #eef0f3",
                borderRadius: 12,
                padding: "20px 24px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 18 }}>{icon}</span>
                {badge && (
                    <span
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 20,
                            background: badgeColor?.bg || "#fef3c7",
                            color: badgeColor?.text || "#b45309",
                        }}
                    >
                        {badge}
                    </span>
                )}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>{value}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{label}</div>
            {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
        </div>
    );
}

// ── mini bar chart ────────────────────────────────────────────────────────────

function BarChart({ data, color = "#6366f1", height = 100 }) {
    if (!data?.length) return null;
    const max = Math.max(...data.map((d) => d.value), 1);
    return (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height }}>
            {data.map((d, i) => (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <div
                        style={{
                            width: "100%",
                            height: Math.max(4, (d.value / max) * (height - 20)),
                            background: color,
                            borderRadius: "3px 3px 0 0",
                            transition: "height 0.4s",
                        }}
                        title={`${d.label}: ${d.value}`}
                    />
                    <div style={{ fontSize: 9, color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                        {d.label}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── row list ──────────────────────────────────────────────────────────────────

function RowList({ rows, columns }) {
    return (
        <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                    <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                        {columns.map((c) => (
                            <th key={c.key} style={{ padding: "8px 12px", textAlign: c.align || "left", color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>
                                {c.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                            {columns.map((c) => (
                                <td key={c.key} style={{ padding: "8px 12px", textAlign: c.align || "left", color: "#111827" }}>
                                    {c.render ? c.render(row[c.key], row) : row[c.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={columns.length} style={{ padding: "24px 12px", textAlign: "center", color: "#9ca3af" }}>
                                No data
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// ── lozenge ───────────────────────────────────────────────────────────────────

function Lozenge({ children, color }) {
    const palette = {
        red: { bg: "#fee2e2", text: "#b91c1c" },
        yellow: { bg: "#fef9c3", text: "#a16207" },
        green: { bg: "#dcfce7", text: "#15803d" },
        blue: { bg: "#dbeafe", text: "#1d4ed8" },
        gray: { bg: "#f3f4f6", text: "#374151" },
    };
    const c = palette[color] || palette.gray;
    return (
        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: c.bg, color: c.text }}>
            {children}
        </span>
    );
}

// ── chart constants ──────────────────────────────────────────────────────────

const STATUS_COLORS = ["#4ade80", "#86efac", "#fb923c", "#818cf8", "#f87171", "#38bdf8", "#fbbf24", "#c084fc"];

const DONUT_OPTIONS = {
    cutout: "70%",
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
};

const COMPLETION_OPTIONS = {
    maintainAspectRatio: false,
    plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true, font: { size: 12 } } },
    },
    scales: {
        y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "#f3f4f6" } },
        x: { grid: { display: false } },
    },
};

// ── main component ─────────────────────────────────────────────────────────────

export default function ProjectPulsePanel({ data, filters }) {
    const { tasks = [], projects = [], timeLogs = [], users = [], projectUsers = [] } = data || {};
    const { projectId, status } = filters || {};

    // ── filter ──
    const filteredTasks = useMemo(() => {
        let t = tasks;
        if (projectId) t = t.filter((tk) => tk.project?.id === projectId || tk.project_id === projectId);
        if (status && typeof status === "string") t = t.filter((tk) => (tk.status?.name || "").toLowerCase() === status.toLowerCase());
        return t;
    }, [tasks, projectId, status]);

    const filteredProjects = useMemo(() => {
        let p = projects;
        if (projectId) p = p.filter((pr) => pr.id === projectId);
        return p;
    }, [projects, projectId]);

    const filteredTimeLogs = useMemo(() => {
        const days = timeLogs;
        if (!projectId) return days;
        return days
            .map((day) => ({
                ...day,
                log_details: (day.log_details || []).filter((d) => d.project?.id === projectId),
            }))
            .filter((day) => day.log_details.length > 0);
    }, [timeLogs, projectId]);

    // ── aggregates ──

    const openTasks = filteredTasks.filter((t) => !t.is_completed);
    const closedTasks = filteredTasks.filter((t) => t.is_completed);
    const today = new Date();
    const overdueTasks = openTasks.filter((t) => {
        if (!t.end_date) return false;
        return new Date(t.end_date) < today;
    });

    const activeProjects = filteredProjects.filter((p) => {
        const s = (p.status?.name || p.status || "").toLowerCase();
        return s === "active" || s === "in progress" || s === "";
    });

    // Open issues = high-priority open tasks
    const highPriorityOpen = openTasks.filter((t) => {
        const p = (t.priority || "").toLowerCase();
        return p === "high" || p === "critical" || p === "urgent";
    });

    // Tasks added this week
    const weekAgo = new Date(today - 7 * 24 * 3600 * 1000);
    const newThisWeek = openTasks.filter((t) => t.created_time && new Date(t.created_time) >= weekAgo);

    const overdueRatio = openTasks.length > 0 ? ((overdueTasks.length / openTasks.length) * 100).toFixed(1) : "0.0";
    const closureRate = filteredTasks.length > 0 ? ((closedTasks.length / filteredTasks.length) * 100).toFixed(1) : "0.0";

    // Users at risk = assigned to >= 5 open tasks
    const tasksByAssignee = {};
    openTasks.forEach((t) => {
        const uid = t.owner?.id || t.assignee_id || t.created_by;
        if (uid) tasksByAssignee[uid] = (tasksByAssignee[uid] || 0) + 1;
    });
    const atRiskUsers = Object.values(tasksByAssignee).filter((c) => c >= 5).length;

    // Time log summary
    const allLogDetails = filteredTimeLogs.flatMap((d) => d.log_details || []);
    const totalBillable = allLogDetails.filter((l) => l.billing_status === "Billable").reduce((s, l) => s + parseHours(l.log_hour), 0);
    const totalNonBillable = allLogDetails.filter((l) => l.billing_status !== "Billable").reduce((s, l) => s + parseHours(l.log_hour), 0);

    // Tasks by project chart
    const tasksByProject = useMemo(() => {
        const map = {};
        filteredTasks.forEach((t) => {
            const name = t.project?.name || t.project_name || "Unknown";
            map[name] = (map[name] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, value]) => ({ label, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    }, [filteredTasks]);

    // Hours by project chart
    const hoursByProject = useMemo(() => {
        const map = {};
        allLogDetails.forEach((l) => {
            const name = l.project?.name || "Unknown";
            map[name] = (map[name] || 0) + parseHours(l.log_hour);
        });
        return Object.entries(map)
            .map(([label, value]) => ({ label, value: Math.round(value * 10) / 10 }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    }, [allLogDetails]);

    // Top overdue tasks table
    const overdueRows = overdueTasks
        .sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
        .slice(0, 8)
        .map((t) => ({
            name: t.name,
            project: t.project?.name || "",
            due: t.end_date,
            priority: t.priority || "Normal",
            days: Math.ceil((today - new Date(t.end_date)) / 86400000),
        }));

    // Top users by logged hours
    const userHoursMap = {};
    allLogDetails.forEach((l) => {
        const name = l.owner?.name || "Unknown";
        userHoursMap[name] = (userHoursMap[name] || 0) + parseHours(l.log_hour);
    });
    const topUsers = Object.entries(userHoursMap)
        .map(([name, hours]) => ({ name, hours }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 8)
        .map((u) => ({ ...u, hoursStr: fmtHours(u.hours) }));

    // ── new widgets data ──
    const [statusTimeframe, setStatusTimeframe] = useState("current_month");

    // Project-filtered tasks (no status filter) for cross-cutting charts
    const projectTasks = useMemo(() => {
        if (!projectId) return tasks;
        return tasks.filter((t) => t.project?.id === projectId || t.project_id === projectId);
    }, [tasks, projectId]);

    // Open tasks by user
    const tasksByUser = useMemo(() => {
        const open = projectTasks.filter((t) => !t.is_completed);
        const map = {};
        open.forEach((t) => {
            const name = t.owner?.name || "Unknown";
            if (!map[name]) map[name] = { name, total: 0, overdue: 0 };
            map[name].total++;
            if (t.end_date && new Date(t.end_date) < today) map[name].overdue++;
        });
        return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 8);
    }, [projectTasks]);

    // Status breakdown (timeframe-aware)
    const statusBreakdown = useMemo(() => {
        let base = projectTasks;
        if (statusTimeframe !== "all") {
            const cutoff = statusTimeframe === "current_month"
                ? new Date(today.getFullYear(), today.getMonth(), 1)
                : new Date(today.getFullYear(), today.getMonth() - 3, 1);
            base = base.filter((t) => !t.created_time || new Date(t.created_time) >= cutoff);
        }
        const map = {};
        base.forEach((t) => {
            const name = t.status?.name || "Unknown";
            map[name] = (map[name] || 0) + 1;
        });
        return Object.entries(map)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count);
    }, [projectTasks, statusTimeframe]);

    // Monthly completion vs target (last 6 months)
    const monthlyCompletion = useMemo(() => {
        const months = [];
        for (let i = 5; i >= 0; i--) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            months.push({ label: d.toLocaleString("en-US", { month: "short" }), year: d.getFullYear(), month: d.getMonth(), completed: 0 });
        }
        projectTasks.filter((t) => t.is_completed).forEach((t) => {
            const raw = t.updated_time || t.end_date || t.created_time;
            if (!raw) return;
            const d = new Date(raw);
            const m = months.find((m) => m.year === d.getFullYear() && m.month === d.getMonth());
            if (m) m.completed++;
        });
        const avg = months.reduce((s, m) => s + m.completed, 0) / (months.length || 1);
        const target = Math.max(10, Math.round(avg * 1.1));
        return months.map((m) => ({ ...m, target }));
    }, [projectTasks]);

    const donutChartData = useMemo(() => ({
        labels: statusBreakdown.map((s) => s.name),
        datasets: [{
            data: statusBreakdown.map((s) => s.count),
            backgroundColor: statusBreakdown.map((_, i) => STATUS_COLORS[i % STATUS_COLORS.length]),
            borderWidth: 0,
            hoverOffset: 4,
        }],
    }), [statusBreakdown]);

    const completionChartData = useMemo(() => ({
        labels: monthlyCompletion.map((m) => m.label),
        datasets: [
            {
                type: "bar",
                label: "Completed Tasks",
                data: monthlyCompletion.map((m) => m.completed),
                backgroundColor: "#6366f1",
                borderRadius: 4,
                order: 2,
            },
            {
                type: "line",
                label: "Target Tasks",
                data: monthlyCompletion.map((m) => m.target),
                borderColor: "#10b981",
                pointBackgroundColor: "#10b981",
                pointBorderColor: "#fff",
                pointBorderWidth: 2,
                pointRadius: 5,
                fill: false,
                tension: 0.4,
                order: 1,
            },
        ],
    }), [monthlyCompletion]);

    const userInsight = useMemo(() => {
        if (!tasksByUser.length) return "";
        const topByTotal = tasksByUser[0];
        const topByOverdue = [...tasksByUser].sort((a, b) => b.overdue - a.overdue)[0];
        if (topByTotal.name === topByOverdue.name && topByTotal.overdue > 0) {
            return `${topByTotal.name} owns the highest task load and the highest overdue count across active projects.`;
        }
        const overdueNote = topByOverdue.overdue > 0 ? ` ${topByOverdue.name} leads in overdue tasks (${topByOverdue.overdue}).` : "";
        return `${topByTotal.name} has the most open tasks (${topByTotal.total}).${overdueNote}`;
    }, [tasksByUser]);

    const completionInsight = useMemo(() => {
        const below = monthlyCompletion.filter((m) => m.completed > 0 && m.completed < m.target);
        if (!below.length) {
            return monthlyCompletion.every((m) => m.completed === 0) ? "" : "Task completion is on track or exceeding targets across all tracked months.";
        }
        const names = below.map((m) => m.label);
        const listed = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
        return `Task completion dropped below target in ${listed}, indicating possible delivery slowdown.`;
    }, [monthlyCompletion]);

    const priorityColor = (p) => {
        const l = (p || "").toLowerCase();
        if (l === "high" || l === "critical" || l === "urgent") return "red";
        if (l === "medium") return "yellow";
        if (l === "low") return "green";
        return "gray";
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

            {/* ── stat cards ── */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <StatCard
                    icon="🕐"
                    badge={newThisWeek.length > 0 ? `+${newThisWeek.length} this week` : null}
                    badgeColor={{ bg: "#dbeafe", text: "#1d4ed8" }}
                    value={openTasks.length.toLocaleString()}
                    label="Open Tasks"
                    sub={`Across ${activeProjects.length} active projects`}
                />
                <StatCard
                    icon="⚠️"
                    badge={`${overdueRatio}% overdue ratio`}
                    badgeColor={{ bg: "#fee2e2", text: "#b91c1c" }}
                    value={overdueTasks.length.toLocaleString()}
                    label="Overdue Tasks"
                    sub="High delivery risk"
                />
                <StatCard
                    icon="✅"
                    badge={`${closureRate}% closure rate`}
                    badgeColor={{ bg: "#dcfce7", text: "#15803d" }}
                    value={closedTasks.length.toLocaleString()}
                    label="Closed Tasks"
                    sub="All-time completed"
                />
                <StatCard
                    icon="👥"
                    badge={atRiskUsers > 0 ? `${atRiskUsers} at risk` : null}
                    badgeColor={{ bg: "#fef9c3", text: "#a16207" }}
                    value={activeProjects.length.toLocaleString()}
                    label="Active Projects"
                    sub={`${filteredProjects.length - activeProjects.length} on track`}
                />
                <StatCard
                    icon="🔴"
                    badge={highPriorityOpen.length > 0 ? `${highPriorityOpen.length} high priority` : null}
                    badgeColor={{ bg: "#fee2e2", text: "#b91c1c" }}
                    value={highPriorityOpen.length.toLocaleString()}
                    label="Open Issues"
                    sub="Requires attention"
                />
            </div>

            {/* ── charts row ── */}
            <div style={{ display: "none", gap: 16 }}>
                <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #eef0f3", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                    <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 14 }}>Tasks by Project</div>
                    <BarChart data={tasksByProject} color="#6366f1" height={120} />
                </div>
                <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #eef0f3", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>Logged Hours by Project</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 12 }}>
                        Billable: <strong>{fmtHours(totalBillable)}h</strong> &nbsp;|&nbsp; Non-billable: <strong>{fmtHours(totalNonBillable)}h</strong>
                    </div>
                    <BarChart data={hoursByProject} color="#10b981" height={120} />
                </div>
            </div>

            {/* ── tables row ── */}
            <div style={{ display: "none", gap: 16 }}>
                {/* Overdue tasks */}
                <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #eef0f3", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", minWidth: 0 }}>
                    <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 14 }}>Most Overdue Tasks</div>
                    <RowList
                        rows={overdueRows}
                        columns={[
                            { key: "name", header: "Task" },
                            { key: "project", header: "Project" },
                            { key: "due", header: "Due" },
                            {
                                key: "priority",
                                header: "Priority",
                                render: (v) => <Lozenge color={priorityColor(v)}>{v}</Lozenge>,
                            },
                            {
                                key: "days",
                                header: "Days late",
                                align: "right",
                                render: (v) => <span style={{ color: "#b91c1c", fontWeight: 700 }}>{v}d</span>,
                            },
                        ]}
                    />
                </div>

                {/* Top users by hours */}
                <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #eef0f3", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", minWidth: 0 }}>
                    <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 14 }}>Top Contributors (Hours Logged)</div>
                    <RowList
                        rows={topUsers}
                        columns={[
                            { key: "name", header: "User" },
                            { key: "hoursStr", header: "Total", align: "right" },
                        ]}
                    />
                </div>
            </div>

            {/* ── AI Operational Insights ── */}
            <AiOperationalInsights />

            {/* ── open tasks by user + status breakdown ── */}
            <div style={{ display: "flex", gap: 16 }}>
                {/* Open Tasks by User */}
                <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #eef0f3", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Open Tasks by User</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 16 }}>Current workload distribution across project team members.</div>
                    {tasksByUser.length === 0 && <div style={{ fontSize: 12, color: "#9ca3af", padding: "12px 0" }}>No open tasks found.</div>}
                    {tasksByUser.map((user, i) => {
                        const bgColors = ["#ddd6fe", "#bfdbfe", "#bbf7d0", "#fde68a", "#fecaca", "#e0f2fe", "#f3e8ff", "#ffedd5"];
                        const txColors = ["#7c3aed", "#1d4ed8", "#15803d", "#d97706", "#b91c1c", "#0369a1", "#7e22ce", "#c2410c"];
                        const ci = i % bgColors.length;
                        const maxTotal = tasksByUser[0]?.total || 1;
                        return (
                            <div key={user.name} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                                <div style={{ width: 28, height: 28, borderRadius: "50%", background: bgColors[ci], color: txColors[ci], fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    {(user.name || "?")[0].toUpperCase()}
                                </div>
                                <div style={{ minWidth: 90, flexShrink: 0 }}>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{user.name}</span>
                                    {user.overdue > 0 && (
                                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 20, background: "#fee2e2", color: "#b91c1c" }}>
                                            {user.overdue} overdue
                                        </span>
                                    )}
                                </div>
                                <div style={{ flex: 1, height: 8, background: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
                                    <div style={{ width: `${(user.total / maxTotal) * 100}%`, height: "100%", background: "#1e1e2e", borderRadius: 4 }} />
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 13, minWidth: 24, textAlign: "right" }}>{user.total}</div>
                            </div>
                        );
                    })}
                    {userInsight && (
                        <div style={{ marginTop: 8, padding: "10px 14px", background: "#f0f6ff", borderRadius: 8, fontSize: 12, color: "#374151", display: "flex", gap: 8, alignItems: "flex-start" }}>
                            <span style={{ color: "#6366f1", fontSize: 14, flexShrink: 0 }}>✦</span>
                            <span>{userInsight}</span>
                        </div>
                    )}
                </div>

                {/* Task Status Breakdown */}
                <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #eef0f3", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>Task Status Breakdown</div>
                        <select value={statusTimeframe} onChange={(e) => setStatusTimeframe(e.target.value)} style={{ fontSize: 11, border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 8px", background: "#fff", cursor: "pointer", color: "#374151" }}>
                            <option value="current_month">Current Month</option>
                            <option value="last_3_months">Last 3 Months</option>
                            <option value="all">All Time</option>
                        </select>
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 16 }}>Task status distribution for the selected timeframe.</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <div style={{ width: 160, height: 160, flexShrink: 0 }}>
                            {statusBreakdown.length > 0
                                ? <Chart type="doughnut" data={donutChartData} options={DONUT_OPTIONS} style={{ width: "100%", height: "100%" }} />
                                : <div style={{ fontSize: 12, color: "#9ca3af", paddingTop: 60, textAlign: "center" }}>No data</div>
                            }
                        </div>
                        <div style={{ flex: 1 }}>
                            {statusBreakdown.map((s, i) => (
                                <div key={s.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, fontSize: 12 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: STATUS_COLORS[i % STATUS_COLORS.length], flexShrink: 0 }} />
                                        <span style={{ color: "#374151" }}>{s.name}</span>
                                    </div>
                                    <span style={{ fontWeight: 600, color: "#111827" }}>{s.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div style={{ marginTop: 12, fontSize: 11, color: "#9ca3af" }}>
                        {statusTimeframe === "current_month" && "Showing current month data to avoid all-time completed tasks dominating the distribution."}
                        {statusTimeframe === "last_3_months" && "Showing last 3 months data."}
                        {statusTimeframe === "all" && "Showing all-time data."}
                    </div>
                </div>
            </div>

            {/* ── tasks completed vs target ── */}
            <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #eef0f3", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Tasks Completed vs Target</div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 16 }}>Monthly task completion compared to expected delivery pace.</div>
                <div style={{ height: 280 }}>
                    <Chart type="bar" data={completionChartData} options={COMPLETION_OPTIONS} style={{ height: "100%" }} />
                </div>
                {completionInsight && (
                    <div style={{ marginTop: 16, padding: "10px 14px", background: "#f0f6ff", borderRadius: 8, fontSize: 12, color: "#374151", display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <span style={{ color: "#6366f1", fontSize: 14, flexShrink: 0 }}>✦</span>
                        <span>{completionInsight}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
